/**
 * useUnlockReturn — waits on the webhook's unlock after an unlock checkout
 * (#1775, #1776).
 *
 * Stripe sends the buyer back to `#/scan/:id?session_id=…` as soon as the
 * payment clears, which can be before `checkout.session.completed` has
 * unlocked the report. While a session id is present this polls the report
 * itself (never the credit balance: an unlock purchase nets to zero) every
 * 2 s for up to 30 s, and hands the unlocked result over exactly once.
 * A failed read (a 401 before Clerk has a token, a network blip) keeps
 * polling; past the ceiling the status is `timeout` and `retry()` re-checks.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScanResult } from '../utils/types';

export const UNLOCK_POLL_INTERVAL_MS = 2000;
export const UNLOCK_POLL_TIMEOUT_MS = 30000;

export type UnlockReturnStatus = 'idle' | 'waiting' | 'unlocked' | 'timeout';

interface UseUnlockReturnArgs {
  sessionId: string | null;
  scanId: string | null;
  /** False until there is an account to read the report as. */
  enabled: boolean;
  load: (scanId: string) => Promise<ScanResult>;
  onUnlocked: (scan: ScanResult) => void;
}

export function useUnlockReturn({ sessionId, scanId, enabled, load, onUnlocked }: UseUnlockReturnArgs) {
  const [status, setStatus] = useState<UnlockReturnStatus>(sessionId && scanId ? 'waiting' : 'idle');
  const [attempt, setAttempt] = useState(0);
  // Read through refs so a caller's fresh closures never restart the poll.
  const loadRef = useRef(load);
  loadRef.current = load;
  const onUnlockedRef = useRef(onUnlocked);
  onUnlockedRef.current = onUnlocked;
  // One hand-over per session, whatever re-renders or StrictMode do.
  const deliveredRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionId || !scanId) {
      setStatus('idle');
      return;
    }
    if (deliveredRef.current === sessionId) return;
    setStatus('waiting');
    if (!enabled) return;

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const scan = await loadRef.current(scanId);
        if (stopped) return;
        if (scan.licenseStatus === 'unlocked') {
          deliveredRef.current = sessionId;
          setStatus('unlocked');
          onUnlockedRef.current(scan);
          return;
        }
      } catch {
        // Keep polling: the token or the network may simply not be ready.
      }
      if (stopped) return;
      if (Date.now() - startedAt >= UNLOCK_POLL_TIMEOUT_MS) {
        setStatus('timeout');
        return;
      }
      timer = setTimeout(poll, UNLOCK_POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [sessionId, scanId, enabled, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { status, retry };
}
